import { Fragment, useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Polyline, CircleMarker, Marker, Tooltip } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useTheme } from "next-themes";

const truckIcon = (color) =>
  L.divIcon({
    className: "",
    iconSize: [30, 30],
    iconAnchor: [15, 15],
    html: `<div style="width:30px;height:30px;border-radius:9999px;background:${color};display:flex;align-items:center;justify-content:center;box-shadow:0 0 0 4px ${color}33,0 4px 10px rgba(0,0,0,.4)">
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M10 17h4V5H2v12h3"/><path d="M20 17h2v-3.34a4 4 0 0 0-1.17-2.83L19 9h-5"/><circle cx="7.5" cy="17.5" r="1.5"/><circle cx="17.5" cy="17.5" r="1.5"/></svg>
    </div>`,
  });

const liveMarkerIcon = L.divIcon({
  className: "",
  iconSize: [26, 26],
  iconAnchor: [13, 13],
  html: `<div style="width:16px;height:16px;border-radius:9999px;background:#22c55e;border:3px solid #fff;box-shadow:0 0 0 6px rgba(34,197,94,.25),0 0 14px rgba(34,197,94,.9)"></div>`,
});

const lerp = (a, b, t) => a + (b - a) * t;

function buildPath(cities, perSeg = 44) {
  const pts = [];
  for (let i = 0; i < cities.length - 1; i++) {
    const a = cities[i], b = cities[i + 1];
    for (let s = 0; s < perSeg; s++) {
      const t = s / perSeg;
      pts.push([lerp(a.lat, b.lat, t), lerp(a.lng, b.lng, t)]);
    }
  }
  pts.push([cities[cities.length - 1].lat, cities[cities.length - 1].lng]);
  return pts;
}

function AnimatedTruck({ cities, color, speed = 130 }) {
  const path = useRef(buildPath(cities)).current;
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setI((p) => (p + 1) % path.length), speed);
    return () => clearInterval(id);
  }, [path.length, speed]);
  return <Marker position={path[i]} icon={truckIcon(color)} />;
}

export default function RouteMap({ routes, animate = false, selectedId = null, currentIndexById = {}, live = null, height = "520px", center = [32, -100], zoom = 3 }) {
  const { resolvedTheme } = useTheme();
  const dark = resolvedTheme === "dark";
  const url = dark
    ? "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
    : "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png";

  return (
    <MapContainer
      key={dark ? "d" : "l"}
      center={center}
      zoom={zoom}
      scrollWheelZoom={false}
      style={{ height, width: "100%", background: dark ? "#0b0e13" : "#e9edf2", borderRadius: "1rem" }}
      attributionControl={false}
    >
      <TileLayer url={url} />
      {routes.map((r) => {
        const pos = r.cities.map((c) => [c.lat, c.lng]);
        const ci = currentIndexById[r.id];
        const dim = selectedId && r.id !== selectedId;
        return (
          <Fragment key={r.id}>
            <Polyline positions={pos} pathOptions={{ color: r.color, weight: dim ? 2 : 3.5, opacity: dim ? 0.22 : 0.95, dashArray: "1 9", lineCap: "round" }} />
            {r.cities.map((c, idx) => (
              <CircleMarker key={c.name + idx} center={[c.lat, c.lng]} radius={dim ? 3 : 5}
                pathOptions={{ color: r.color, fillColor: dark ? "#0b0e13" : "#ffffff", fillOpacity: dim ? 0.35 : 1, opacity: dim ? 0.35 : 1, weight: 2 }}>
                <Tooltip direction="top">{c.name}</Tooltip>
              </CircleMarker>
            ))}
            {animate ? (
              (!selectedId || r.id === selectedId) && <AnimatedTruck cities={r.cities} color={r.color} />
            ) : (
              ci != null && r.cities[ci] && <Marker position={[r.cities[ci].lat, r.cities[ci].lng]} icon={truckIcon(r.color)} />
            )}
          </Fragment>
        );
      })}
      {live && live.lat != null && (
        <Marker position={[live.lat, live.lng]} icon={liveMarkerIcon}>
          <Tooltip direction="top">Live driver position</Tooltip>
        </Marker>
      )}
    </MapContainer>
  );
}
